import React, { useState, useEffect, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import DashboardHeader from '../components/DashboardHeader';
import DashboardSidebar from '../components/DashboardSidebar';
import { useAuth } from '../../contexts/AuthContext';
import '../styles/NominationScreen.css';
import { useLocation } from 'react-router-dom';
import { uploadVideoToCloudflare } from '../../utils/cloudflareUpload';
import Cropper from 'react-easy-crop';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92));
}

const NominationScreen = ({ setActiveScreen }) => {
  const {
    getNominations,
    updateNominationStatus,
    createSpotlight,
    createSpotlightDirect,
    uploadImage,
    updateNominationVideoReviewed,
    deleteNomination,
    loading
  } = useAuth();

  const [activeItem, setActiveItem] = useState('nominations');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('requests');
  const [selectedSport, setSelectedSport] = useState('Sports');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
  const [deleteLoading, setDeleteLoading] = useState(false);
  // Image crop state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
const location=useLocation();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showSpotlightModal, setShowSpotlightModal] = useState(false);
  const [nominations, setNominations] = useState([]);
  const [spotlightData, setSpotlightData] = useState({
    athleteName: '',
    primarySport: '',
    city: '',
    state: '',
    heroImage: null,
    heroImagePreview: null,
    nominationVideoUid: null,
    uploadedVideoFile: null,
    uploadedVideoUid: null,
    uploadedVideoPreview: null,
    momentHeadline: 'The Moment',
    regionalLabel: '',
    momentDescription: '',
    momentMedia: [],
    officialCaption: '',
    timeLabel: '',
    selectedHeroImageIndex: null
  });
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalErrors, setModalErrors] = useState({});
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState('');
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [photoGalleryImages, setPhotoGalleryImages] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showCloudflareVideoPreview, setShowCloudflareVideoPreview] = useState(false);
  const [previewCloudflareVideoUid, setPreviewCloudflareVideoUid] = useState('');

  const tabs = [
    { id: 'all', label: 'All', status: null },
    { id: 'requests', label: 'Requests', status: 'pending' },
    { id: 'approved', label: 'Approved', status: 'approved' },
    { id: 'rejected', label: 'Rejected', status: 'rejected' },
    { id: 'live-spotlight', label: 'Live Spotlight', status: 'spotlighted' },
    { id: 'on-hold', label: 'On Hold', status: 'hold' },
  ];

  // Show notification
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 5000);
  };

  // Fetch nominations based on active tab
  useEffect(() => {
    fetchNominations();
  }, [activeTab]);

  const fetchNominations = async () => {
    try {
      const tabConfig = tabs.find(t => t.id === activeTab);
      const status = tabConfig?.status || null;
      const result = await getNominations(status);
      
      if (result.success) {
        setNominations(result.data || []);
      } else {
        showNotification('error', `Failed to load nominations: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching nominations:', error);
      showNotification('error', 'Failed to load nominations. Please try again.');
    }
  };

  // Filter nominations by sport and search
  const filteredNominations = nominations.filter(nom => {
    const matchesSport = selectedSport === 'Sports' || nom.primary_sport === selectedSport;
    const matchesSearch = searchQuery === '' || 
      `${nom.first_name} ${nom.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      nom.primary_sport?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSport && matchesSearch;
  });

  const handleReviewClick = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
    setModalErrors({});
  };

  const handleDeleteNomination = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    const result = await deleteNomination(deleteConfirm.id);
    setDeleteLoading(false);
    setDeleteConfirm(null);
    if (result.success) {
      setNominations(prev => prev.filter(n => n.id !== deleteConfirm.id));
      showNotification('success', 'Nomination removed from queue.');
    } else {
      showNotification('error', result.error || 'Failed to delete nomination.');
    }
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleCropConfirm = async () => {
    try {
      const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
      const preview = URL.createObjectURL(blob);
      const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
      setSpotlightData(prev => {
        const updated = [...(prev.momentMedia || []), { file, preview, type: 'image' }];
        return {
          ...prev,
          momentMedia: updated,
          selectedHeroImageIndex: prev.selectedHeroImageIndex === null ? 0 : prev.selectedHeroImageIndex,
        };
      });
    } catch (e) {
      console.error('Crop error:', e);
    } finally {
      setCropModalOpen(false);
      setRawImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    setModalErrors({});
  };

  // YouTube helper functions
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleVideoPreview = (url) => {
    setPreviewVideoUrl(url);
    setShowVideoPreview(true);
  };

  const handlePhotoGalleryOpen = (photos) => {
    setPhotoGalleryImages(photos);
    setCurrentPhotoIndex(0);
    setShowPhotoGallery(true);
  };

  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photoGalleryImages.length);
  };

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photoGalleryImages.length) % photoGalleryImages.length);
  };

  const handleCloudflareVideoPreview = (uid) => {
    setPreviewCloudflareVideoUid(uid);
    setShowCloudflareVideoPreview(true);
  };

  const handleCopyYouTubeLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      showNotification('success', 'YouTube link copied to clipboard!');
    } catch (err) {
      showNotification('error', 'Failed to copy link');
    }
  };

  const handleToggleVideoReviewed = async (nominationId, currentStatus) => {
    try {
      const result = await updateNominationVideoReviewed(nominationId, !currentStatus);
      if (result.success) {
        // Update local state
        setNominations(prev => prev.map(nom =>
          nom.id === nominationId ? { ...nom, video_reviewed: !currentStatus } : nom
        ));
        if (selectedRequest && selectedRequest.id === nominationId) {
          setSelectedRequest({ ...selectedRequest, video_reviewed: !currentStatus });
        }
        showNotification('success', `Video marked as ${!currentStatus ? 'reviewed' : 'not reviewed'}`);
      }
    } catch (error) {
      showNotification('error', 'Failed to update video status');
    }
  };

  const handleOpenSpotlightModal = (nomination = null) => {
    if (nomination) {
      setSelectedRequest(nomination);

      // Pre-populate images from nomination
      const nominationImages = nomination.athlete_photos && nomination.athlete_photos.length > 0
        ? nomination.athlete_photos.map(url => ({ url, type: 'image', fromNomination: true }))
        : [];

      setSpotlightData({
        athleteName: `${nomination.first_name} ${nomination.last_name}`,
        primarySport: nomination.primary_sport || '',
        city: nomination.city || '',
        state: nomination.state || '',
        heroImage: null,
        heroImagePreview: null,
        nominationVideoUid: nomination.video_uid || null, // Store nomination video
        uploadedVideoFile: null,
        uploadedVideoUid: null,
        uploadedVideoPreview: null,
        momentHeadline: 'The Moment',
        regionalLabel: '',
        momentDescription: '',
        momentMedia: nominationImages, // Pre-populate with nomination images
        officialCaption: '',
        timeLabel: '',
        selectedHeroImageIndex: nominationImages.length > 0 ? 0 : null // Auto-select first image as hero
      });
      setVideoUploadProgress(0);
      setIsUploadingVideo(false);
    } else {
      setSelectedRequest(null);
      setSpotlightData({
        athleteName: '',
        primarySport: '',
        city: '',
        state: '',
        heroImage: null,
        heroImagePreview: null,
        nominationVideoUid: null,
        uploadedVideoFile: null,
        uploadedVideoUid: null,
        uploadedVideoPreview: null,
        momentHeadline: 'The Moment',
        regionalLabel: '',
        momentDescription: '',
        momentMedia: [],
        officialCaption: '',
        timeLabel: '',
        selectedHeroImageIndex: null
      });
      setVideoUploadProgress(0);
      setIsUploadingVideo(false);
    }
    setModalErrors({});
    setShowSpotlightModal(true);
  };

  const handleCloseSpotlightModal = () => {
    setShowSpotlightModal(false);
    setModalErrors({});
  };

  // Handle status update (approve, reject, hold)
  const handleStatusUpdate = async (status) => {
    if (!selectedRequest) {
      showNotification('error', 'No request selected');
      return;
    }
    
    setActionLoading(true);
    
    try {
      const result = await updateNominationStatus(selectedRequest.id, status);
      
      if (result.success) {
        showNotification('success', `Nomination ${status} successfully`);
        handleCloseModal();
        fetchNominations();
      } else {
        showNotification('error', `Failed to update status: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification('error', 'Failed to update nomination status');
    } finally {
      setActionLoading(false);
    }
  };

  // Helper function to validate YouTube URL
  const isValidYouTubeUrl = (url) => {
    if (!url) return false;
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]+/;
    return youtubeRegex.test(url);
  };

  const validateSpotlight = () => {
    const newErrors = {};

    // Validation for direct creation (no nomination)
    if (!selectedRequest) {
      if (!spotlightData.athleteName?.trim()) {
        newErrors.athleteName = 'Athlete name is required';
      }
    }

    if (!spotlightData.primarySport?.trim()) {
      newErrors.primarySport = 'Primary sport is required';
    }

    // Hero image validation - check if one is selected from moment media
    if (spotlightData.selectedHeroImageIndex === null) {
      newErrors.heroImage = 'Please select one image as the hero image';
    }

    if (!spotlightData.officialCaption?.trim()) {
      newErrors.officialCaption = 'Official caption is required';
    }

    // Validate moment media - At least 1 image required (can be from nomination or uploaded)
    if (!spotlightData.momentMedia || spotlightData.momentMedia.length === 0) {
      newErrors.momentMedia = 'At least 1 image is required';
    } else {

      // Validate individual image files
      spotlightData.momentMedia.forEach((media, index) => {
        if (media.file) {
          const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
          if (!validTypes.includes(media.file.type)) {
            newErrors[`momentMedia_${index}`] = `Invalid file type for image ${index + 1}`;
          }

          // Validate file size (5MB for images)
          const maxSize = 5 * 1024 * 1024;
          if (media.file.size > maxSize) {
            newErrors[`momentMedia_${index}`] = `Image ${index + 1} exceeds 5MB limit`;
          }
        }
      });
    }

    setModalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  useEffect(() => {
    if (setActiveItem) {
      if (location.pathname.includes('/dashboard/nominations')) {
        setActiveItem('nominations');
      } else if (location.pathname.includes('/dashboard/featured')) {
        setActiveItem('athletes');
      } else if (location.pathname.includes('/dashboard/analytics')) {
        setActiveItem('analytics');
      } else if (location.pathname.includes('/dashboard/email-list')) {
        setActiveItem('email-list');
      }
    }
  }, [location.pathname, setActiveItem]);
  // Handle create spotlight
 const handleCreateSpotlight = async () => {
  // Validate form first
  if (!validateSpotlight()) {
    // Scroll to first error
    setTimeout(() => {
      const firstError = Object.keys(modalErrors)[0];
      if (firstError) {
        const element = document.querySelector(`[data-error-field="${firstError}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
    return;
  }

  setActionLoading(true);

  try {
    // Upload video first if there's a file to upload
    let uploadedVideoUid = null;
    if (spotlightData.uploadedVideoFile) {
      setIsUploadingVideo(true);
      setVideoUploadProgress(0);

      try {
        showNotification('success', 'Uploading video...');
        const videoResult = await uploadVideoToCloudflare(
          spotlightData.uploadedVideoFile,
          setVideoUploadProgress
        );

        if (videoResult && videoResult.uid) {
          uploadedVideoUid = videoResult.uid;
          showNotification('success', 'Video uploaded successfully!');
        } else {
          throw new Error('Video upload failed');
        }
      } catch (videoError) {
        console.error('Video upload error:', videoError);
        showNotification('error', 'Failed to upload video. Please try again.');
        setActionLoading(false);
        setIsUploadingVideo(false);
        setVideoUploadProgress(0);
        return;
      } finally {
        setIsUploadingVideo(false);
      }
    }

    // Hero image will be extracted from moment media after all images are uploaded
    let heroImageUrl = null;

    // Process moment media - keep existing URLs or upload new files
    // Upload all images first
    let allProcessedImages = [];
    if (spotlightData.momentMedia && spotlightData.momentMedia.length > 0) {
      for (const media of spotlightData.momentMedia) {
        if (media.url && media.fromNomination) {
          // Keep existing URL from nomination
          allProcessedImages.push(media.url);
        } else if (media.file) {
          // Upload new file
          try {
            const uploadResult = await uploadImage(media.file);
            if (uploadResult.success) {
              allProcessedImages.push(uploadResult.url);
            }
          } catch (error) {
            console.warn('Error uploading image:', error);
          }
        }
      }
    }

    // Separate hero image from moment media
    // Hero image should NOT be in moment_media array
    let finalMomentMedia = [];
    if (spotlightData.selectedHeroImageIndex !== null && allProcessedImages[spotlightData.selectedHeroImageIndex]) {
      // Extract hero image from the selected index
      heroImageUrl = allProcessedImages[spotlightData.selectedHeroImageIndex];

      // Remove hero image from moment_media array
      finalMomentMedia = allProcessedImages.filter((_, index) => index !== spotlightData.selectedHeroImageIndex);
    }

    // Get video UID (prioritize newly uploaded video, then nomination video)
    const videoUid = uploadedVideoUid || spotlightData.nominationVideoUid || null;

    console.log('Creating spotlight with:', {
      heroImage: heroImageUrl,
      videoUid: videoUid,
      momentMedia: finalMomentMedia
    });

    let result;

    if (selectedRequest) {
      // Create from nomination
      result = await createSpotlight(selectedRequest.id, {
        heroImage: heroImageUrl,
        videoUid: videoUid,
        momentHeadline: 'The Moment',
        regionalLabel: spotlightData.regionalLabel || '',
        momentDescription: 'This athlete earned their spotlight through consistency, leadership, and showing up when it mattered.',
        momentMedia: finalMomentMedia,
        officialCaption: spotlightData.officialCaption,
        timeLabel: spotlightData.timeLabel
      });

      // IMPORTANT: If spotlight created but nomination status update failed,
      // manually update the nomination status
      if (result.success && !result.nominationUpdated) {
        console.log('Spotlight created but nomination status not updated, updating manually...');
        await updateNominationStatus(selectedRequest.id, 'spotlighted');
      }
    } else {
      // Create directly without nomination
      result = await createSpotlightDirect({
        athleteName: spotlightData.athleteName.trim(),
        primarySport: spotlightData.primarySport.trim(),
        city: spotlightData.city?.trim(),
        state: spotlightData.state?.trim(),
        heroImage: heroImageUrl,
        videoUid: videoUid,
        momentHeadline: 'The Moment',
        regionalLabel: spotlightData.regionalLabel || '',
        momentDescription: 'This athlete earned their spotlight through consistency, leadership, and showing up when it mattered.',
        momentMedia: finalMomentMedia,
        officialCaption: spotlightData.officialCaption,
        timeLabel: spotlightData.timeLabel
      });
    }

    console.log('Create spotlight result:', result);

    if (result.success) {
      showNotification('success', 'Spotlight created successfully!');
      handleCloseSpotlightModal();
      handleCloseModal();
      
      // IMPORTANT: Force refresh the nominations list
      await fetchNominations();
      
      // Also switch to the spotlighted tab to see the updated nomination
      setActiveTab('live-spotlight');
    } else {
      throw new Error(result.error || 'Failed to create spotlight');
    }
  } catch (error) {
    console.error('Error creating spotlight:', error);
    showNotification('error', error.message || 'Failed to create spotlight. Please try again.');
  } finally {
    setActionLoading(false);
  }
};

  // Handle hero image upload
  const handleHeroImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validImageTypes.includes(file.type)) {
        setModalErrors(prev => ({
          ...prev,
          heroImage: 'Invalid image format. Please use JPEG, PNG, or WebP'
        }));
        return;
      }
      
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setModalErrors(prev => ({
          ...prev,
          heroImage: 'Image size exceeds 10MB limit'
        }));
        return;
      }

      // Clear any previous error for hero image
      setModalErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.heroImage;
        return newErrors;
      });

      setSpotlightData(prev => ({
        ...prev,
        heroImage: file,
        heroImagePreview: URL.createObjectURL(file)
      }));
    }
  };

  // Handle video selection (preview only, upload happens on publish)
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-m4v'];
    if (!validVideoTypes.includes(file.type)) {
      showNotification('error', 'Invalid video format. Please use MP4 or MOV');
      return;
    }

    // Validate file size (200MB)
    if (file.size > 200 * 1024 * 1024) {
      showNotification('error', 'Video size exceeds 200MB limit');
      return;
    }

    // Just store the file and create preview - don't upload yet
    setSpotlightData(prev => ({
      ...prev,
      uploadedVideoFile: file,
      uploadedVideoPreview: URL.createObjectURL(file),
      nominationVideoUid: null, // Clear nomination video when selecting new one
      uploadedVideoUid: null
    }));
    setVideoUploadProgress(0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  // Clear error when field changes
  const handleFieldChange = (field, value) => {
    setSpotlightData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field if it exists
    if (modalErrors[field]) {
      setModalErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className="nomination-screen">
      {/* Notification Component */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.type === 'success' && '✓ '}
          {notification.type === 'error' && '✗ '}
          {notification.message}
        </div>
      )}

      <DashboardSidebar
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        setActiveScreen={setActiveScreen}
      />

      <div className="nomination-screen-content">
        <DashboardHeader toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

        <Container fluid className="nomination-screen-container">
          {/* Top Controls */}
          <div className="nomination-top-controls">
            <div className="nomination-controls-left">
              {/* Sports Dropdown */}
              <div className="nomination-dropdown">
                <select
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                  className="nomination-dropdown-select"
                >
                  <option>Sports</option>
                  <option>Basketball</option>
                  <option>Soccer</option>
                  <option>Baseball</option>
                  <option>Football</option>
                  <option>Track & Field</option>
                  <option>Swimming</option>
                  <option>Volleyball</option>
                  <option>Tennis</option>
                </select>
                <img
                  src="/assets/images/dropdown-arrow.png"
                  alt=""
                  className="nomination-dropdown-icon"
                />
              </div>

              {/* Search Bar */}
              <div className="nomination-search">
                <img
                  src="/assets/images/search-icon.png"
                  alt="Search"
                  className="nomination-search-icon"
                />
                <input
                  type="text"
                  placeholder="Search"
                  className="nomination-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Add New Button */}
            <button className="nomination-add-btn" onClick={() => handleOpenSpotlightModal()}>
              <img src="/assets/images/plus-icon.png" alt="Add" />
              <span>Add New Spotlight</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="nomination-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`nomination-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="nomination-loading" style={{
              textAlign: 'center',
              padding: '60px 40px'
            }}>
              <p style={{
                color: '#ffffff',
                fontSize: '16px'
              }}>Loading nominations...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredNominations.length === 0 && (
            <div className="nomination-empty" style={{
              textAlign: 'center',
              padding: '60px 40px',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '16px',
              marginTop: '20px'
            }}>
              <p style={{
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '8px'
              }}>No nominations found</p>
              <p style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '14px'
              }}>Try adjusting your filters or check back later</p>
            </div>
          )}

          {/* Nominations Section */}
          {!loading && filteredNominations.length > 0 && (
            <div className="nomination-section">
              <h2 className="nomination-section-title">
                {activeTab === 'requests' ? 'Pending Submissions' : 
                 activeTab === 'approved' ? 'Approved Nominations' :
                 activeTab === 'rejected' ? 'Rejected Nominations' :
                 activeTab === 'live-spotlight' ? 'Live Spotlights' :
                 activeTab === 'on-hold' ? 'On Hold' : 'All Nominations'}
                {' '}({filteredNominations.length})
              </h2>

              {filteredNominations.map((request) => (
                <div key={request.id} className="nomination-submission-card">
                  <div className="nomination-submission-info">
                    <div className="nomination-name-row">
                      <h3 className="nomination-submission-name">{request.first_name} {request.last_name}</h3>
                      <span className={`nomination-status-badge nomination-status-${request.status}`} style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        textTransform: 'capitalize',
                        backgroundColor: request.status === 'pending' ? 'rgba(251, 146, 60, 0.15)' :
                                         request.status === 'approved' ? 'rgba(216, 161, 52, 0.15)' :
                                         request.status === 'rejected' ? 'rgba(239, 68, 68, 0.15)' :
                                         request.status === 'spotlighted' ? 'rgba(52, 199, 89, 0.15)' :
                                         request.status === 'hold' ? 'rgba(147, 51, 234, 0.15)' : 'rgba(156, 163, 175, 0.15)',
                        color: request.status === 'pending' ? '#FB923C' :
                               request.status === 'approved' ? '#D8A134' :
                               request.status === 'rejected' ? '#EF4444' :
                               request.status === 'spotlighted' ? '#34C759' :
                               request.status === 'hold' ? '#A855F7' : '#9CA3AF'
                      }}>
                        {request.status === 'hold' ? 'On Hold' : request.status}
                      </span>
                    </div>

                    <div className="nomination-submission-details">
                      <div className="nomination-detail-item">
                        <img src="/assets/images/sport-icon.png" alt="Sport" />
                        <span>{request.primary_sport || 'N/A'}</span>
                      </div>
                      <div className="nomination-detail-item">
                        <img src="/assets/images/location-icon.png" alt="Location" />
                        <span>{request.city}, {request.state}</span>
                      </div>
                    </div>

                    <div className="nomination-submission-meta">
                      <span>Submitted by: {request.submitter_email}</span>
                      <span>Submitted: {formatDate(request.submitted_at)}</span>
                    </div>
                  </div>

                  <div className="nomination-submission-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="nomination-review-btn"
                      onClick={() => handleReviewClick(request)}
                    >
                      Review
                    </button>
                    <button
                      onClick={() => setDeleteConfirm({ id: request.id, name: `${request.first_name} ${request.last_name}` })}
                      title="Remove from queue"
                      style={{ padding: '8px 10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8px', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Container>
      </div>

      {/* Image Crop Modal */}
      {cropModalOpen && rawImageSrc && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: '480px', height: '480px', borderRadius: '12px', overflow: 'hidden' }}>
            <Cropper
              image={rawImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={3 / 4}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div style={{ marginTop: '20px', width: '100%', maxWidth: '480px', padding: '0 16px' }}>
            <input
              type="range"
              min={1} max={3} step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#f3b129' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button onClick={() => { setCropModalOpen(false); setRawImageSrc(null); }} style={{ padding: '10px 28px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={handleCropConfirm} style={{ padding: '10px 28px', background: '#f3b129', border: 'none', borderRadius: '8px', color: '#000', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
              Crop & Add
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#0e0e0e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px 28px', maxWidth: '380px', width: '90%', textAlign: 'center' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', margin: '0 auto 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.12)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/>
              </svg>
            </div>
            <h2 style={{ color: '#fff', fontSize: '17px', fontWeight: '700', marginBottom: '8px' }}>Remove Nomination?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
              Are you sure you want to permanently remove <span style={{ color: '#fff', fontWeight: '600' }}>{deleteConfirm.name}</span>'s nomination from the queue? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setDeleteConfirm(null)} disabled={deleteLoading} style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleDeleteNomination} disabled={deleteLoading} style={{ flex: 1, padding: '10px', background: '#ef4444', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', opacity: deleteLoading ? 0.7 : 1 }}>
                {deleteLoading ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showModal && selectedRequest && (
        <>
          <div className="nomination-modal-overlay" onClick={handleCloseModal}></div>
          <div className="nomination-modal">
            <div className="nomination-modal-header">
              <div className="nomination-modal-title-section">
                <h2 className="nomination-modal-title">
                  {selectedRequest.first_name} {selectedRequest.last_name} <span className="nomination-modal-id">ID: {selectedRequest.id}</span>
                </h2>
                <p className="nomination-modal-subtitle">Review and manage athlete nominations</p>
              </div>
              <button className="nomination-modal-close" onClick={handleCloseModal}>
                <img src="/assets/images/close-icon.png" alt="Close" />
              </button>
            </div>

            <div className="nomination-modal-body">
              {/* Nomination Type */}
              <div className="nomination-modal-info-section">
                <label className="nomination-modal-label">Nomination Type</label>
                <p className="nomination-modal-value">
                  {selectedRequest.nominator_name ?
                    `Someone Else (Nominated by: ${selectedRequest.nominator_name})` :
                    'Myself (Self-Nomination)'}
                </p>
              </div>

              <div className="nomination-modal-row">
                <div className="nomination-modal-info-section">
                  <label className="nomination-modal-label">Sports</label>
                  <p className="nomination-modal-value">
                    {selectedRequest.primary_sport || 'N/A'}
                    {selectedRequest.secondary_sports ? ` • ${selectedRequest.secondary_sports}` : ''}
                  </p>
                </div>
                <button
                  className="nomination-modal-contact-btn"
                  onClick={() => {
                    if (selectedRequest?.submitter_email) {
                      const subject = encodeURIComponent(`Regarding ${selectedRequest.first_name} ${selectedRequest.last_name}'s Nomination`);
                      const body = encodeURIComponent(`Hi,\n\nI'm reaching out regarding the nomination for ${selectedRequest.first_name} ${selectedRequest.last_name}.\n\n`);
                      window.location.href = `mailto:${selectedRequest.submitter_email}?subject=${subject}&body=${body}`;
                    } else {
                      showNotification('error', 'No email address available');
                    }
                  }}
                >
                  <img src="/assets/images/email-icon1.png" alt="Email" />
                  <span>Contact Submitter</span>
                </button>
              </div>

              <div className="nomination-modal-info-section">
                <label className="nomination-modal-label">Location</label>
                <p className="nomination-modal-value">
                  <img src="/assets/images/location-icon.png" alt="Location" className="nomination-modal-icon" />
                  {selectedRequest.city && selectedRequest.state
                    ? `${selectedRequest.city}, ${selectedRequest.state}`
                    : selectedRequest.city || selectedRequest.state || 'Not Provided'}
                </p>
              </div>

              <div className="nomination-modal-info-section">
                <label className="nomination-modal-label">Contact Information</label>
                <p className="nomination-modal-value">
                  <img src="/assets/images/email-icon1.png" alt="Email" className="nomination-modal-icon" />
                  {selectedRequest.submitter_email || 'Not Provided'}
                </p>
                <p className="nomination-modal-value">
                  <img src="/assets/images/phone-icon.png" alt="Phone" className="nomination-modal-icon" />
                  {selectedRequest.submitter_phone || 'Not Provided'}
                </p>
              </div>

              <div className="nomination-modal-info-section">
                <label className="nomination-modal-label">Why They Should Be Featured</label>
                <div className="nomination-modal-reason">
                  {selectedRequest.reason || 'Not Provided'}
                </div>
              </div>

              {/* Submitted Media Buttons */}
              {((selectedRequest.athlete_photos && selectedRequest.athlete_photos.length > 0) || selectedRequest.video_uid) && (
                <div className="nomination-modal-info-section">
                  <label className="nomination-modal-label">Submitted Media</label>
                  <div className="nomination-media-buttons">
                    {selectedRequest.athlete_photos && selectedRequest.athlete_photos.length > 0 && (
                      <button
                        className="nomination-media-btn"
                        onClick={() => handlePhotoGalleryOpen(selectedRequest.athlete_photos)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <path d="M21 15l-5-5L5 21"/>
                        </svg>
                        View Athlete Photos ({selectedRequest.athlete_photos.length})
                      </button>
                    )}
                    {selectedRequest.video_uid && (
                      <button
                        className="nomination-media-btn"
                        onClick={() => handleCloudflareVideoPreview(selectedRequest.video_uid)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="5 3 19 12 5 21 5 3"/>
                        </svg>
                        View Highlight Video
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* YouTube Video Section */}
              {selectedRequest.youtube_url && (
                <div className="nomination-modal-info-section">
                  <label className="nomination-modal-label">YouTube Video</label>
                  <div className="nomination-youtube-section">
                    <div className="nomination-youtube-link">
                      <img src="/assets/images/youtube-icon.png" alt="YouTube" className="nomination-modal-icon"
                        onError={(e) => { e.target.style.display = 'none'; }} />
                      <a href={selectedRequest.youtube_url} target="_blank" rel="noopener noreferrer" className="nomination-youtube-url">
                        {selectedRequest.youtube_url}
                      </a>
                    </div>
                    <div className="nomination-youtube-actions">
                      <button
                        className="nomination-youtube-btn nomination-preview-btn"
                        onClick={() => handleVideoPreview(selectedRequest.youtube_url)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
                        </svg>
                        Preview
                      </button>
                      <button
                        className="nomination-youtube-btn nomination-copy-btn"
                        onClick={() => handleCopyYouTubeLink(selectedRequest.youtube_url)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                        Copy Link
                      </button>
                      <label className="nomination-reviewed-toggle">
                        <input
                          type="checkbox"
                          checked={selectedRequest.video_reviewed || false}
                          onChange={() => handleToggleVideoReviewed(selectedRequest.id, selectedRequest.video_reviewed)}
                        />
                        <span className="nomination-toggle-slider"></span>
                        <span className="nomination-toggle-label">Reviewed</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="nomination-modal-info-section">
                <label className="nomination-modal-label">Submission Details</label>
                <div className="nomination-modal-submission-details">
                  <p className="nomination-modal-value">Submitted on: {formatDate(selectedRequest.submitted_at)}</p>
                  <div className="nomination-modal-status-row">
                    <span>Status:</span>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'capitalize',
                      marginLeft: '8px',
                      backgroundColor: selectedRequest.status === 'pending' ? 'rgba(251, 146, 60, 0.15)' :
                                       selectedRequest.status === 'approved' ? 'rgba(216, 161, 52, 0.15)' :
                                       selectedRequest.status === 'rejected' ? 'rgba(239, 68, 68, 0.15)' :
                                       selectedRequest.status === 'spotlighted' ? 'rgba(52, 199, 89, 0.15)' :
                                       selectedRequest.status === 'hold' ? 'rgba(147, 51, 234, 0.15)' : 'rgba(156, 163, 175, 0.15)',
                      color: selectedRequest.status === 'pending' ? '#FB923C' :
                             selectedRequest.status === 'approved' ? '#D8A134' :
                             selectedRequest.status === 'rejected' ? '#EF4444' :
                             selectedRequest.status === 'spotlighted' ? '#34C759' :
                             selectedRequest.status === 'hold' ? '#A855F7' : '#9CA3AF'
                    }}>{selectedRequest.status === 'hold' ? 'On Hold' : selectedRequest.status}</span>
                  </div>
                </div>
                <p className="nomination-modal-value">Opted in to updates: {selectedRequest.notify_submitter ? 'Yes' : 'No'}</p>
              </div>
            </div>

            <div className="nomination-modal-footer">
              {/* SPOTLIGHTED: Show message only */}
              {selectedRequest.status === 'spotlighted' ? (
                <div className="nomination-modal-spotlighted-message" style={{
                  width: '100%',
                  textAlign: 'center',
                  padding: '16px',
                  background: 'rgba(52, 199, 89, 0.1)',
                  borderRadius: '8px',
                  color: '#34C759',
                  fontWeight: '500'
                }}>
                  ✓ This athlete has been spotlighted and is now featured
                </div>
              ) : selectedRequest.status === 'rejected' ? (
                /* REJECTED: No action buttons */
                <div style={{
                  width: '100%',
                  textAlign: 'center',
                  padding: '16px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '8px',
                  color: '#EF4444',
                  fontWeight: '500'
                }}>
                  This nomination has been rejected
                </div>
              ) : selectedRequest.status === 'approved' ? (
                /* APPROVED: Only Create Spotlight button */
                <>
                  <div style={{ flex: 1, color: '#D8A134', fontSize: '14px', fontWeight: '500' }}>
                    ✓ Approved - Ready to create spotlight
                  </div>
                  <button
                    className="nomination-modal-create-btn"
                    onClick={() => handleOpenSpotlightModal(selectedRequest)}
                    disabled={actionLoading}
                  >
                    Create Spotlight
                  </button>
                </>
              ) : selectedRequest.status === 'hold' ? (
                /* HOLD: Show Accept, Reject, Create Spotlight (NO Hold button) */
                <>
                  <button
                    className="nomination-modal-approve-btn"
                    onClick={() => handleStatusUpdate('approved')}
                    disabled={actionLoading}
                  >
                    <img src="/assets/images/check-icon.png" alt="Accept" />
                    {actionLoading ? '...' : 'Accept'}
                  </button>
                  <button
                    className="nomination-modal-reject-btn"
                    onClick={() => handleStatusUpdate('rejected')}
                    disabled={actionLoading}
                  >
                    <img src="/assets/images/close-icon.png" alt="Reject" />
                    {actionLoading ? '...' : 'Reject'}
                  </button>
                  <button
                    className="nomination-modal-create-btn"
                    onClick={() => handleOpenSpotlightModal(selectedRequest)}
                    disabled={actionLoading}
                  >
                    Create Spotlight
                  </button>
                </>
              ) : (
                /* PENDING: Show all actions */
                <>
                  <button
                    className="nomination-modal-approve-btn"
                    onClick={() => handleStatusUpdate('approved')}
                    disabled={actionLoading}
                  >
                    <img src="/assets/images/check-icon.png" alt="Approve" />
                    {actionLoading ? '...' : 'Approve'}
                  </button>
                  <button
                    className="nomination-modal-reject-btn"
                    onClick={() => handleStatusUpdate('rejected')}
                    disabled={actionLoading}
                  >
                    <img src="/assets/images/close-icon.png" alt="Reject" />
                    {actionLoading ? '...' : 'Reject'}
                  </button>
                  <button
                    className="nomination-modal-hold-btn"
                    onClick={() => handleStatusUpdate('hold')}
                    disabled={actionLoading}
                  >
                    {actionLoading ? '...' : 'Hold'}
                  </button>
                  <button
                    className="nomination-modal-create-btn"
                    onClick={() => handleOpenSpotlightModal(selectedRequest)}
                    disabled={actionLoading}
                  >
                    Create Spotlight
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Spotlight Editor Modal */}
      {showSpotlightModal && (
        <>
          <div className="nomination-modal-overlay" onClick={handleCloseSpotlightModal}></div>
          <div className="spotlight-editor-modal">
            <div className="spotlight-editor-header">
              <h2 className="spotlight-editor-title">
                Spotlight Editor
                {selectedRequest && ` - ${selectedRequest.first_name} ${selectedRequest.last_name}`}
              </h2>
              <button className="spotlight-editor-close" onClick={handleCloseSpotlightModal}>
                <img src="/assets/images/close-modal-icon.png" alt="Close" />
              </button>
            </div>

            <div className="spotlight-editor-body">
              {/* Athlete Info - Only show when creating directly (no nomination) */}
              {!selectedRequest && (
                <>
                  <div className="spotlight-editor-section">
                    <label className="spotlight-editor-label">Athlete Information</label>
                    <label className="spotlight-editor-sublabel">Athlete Name *</label>
                    <input
                      type="text"
                      className={`spotlight-editor-input ${modalErrors.athleteName ? 'error' : ''}`}
                      placeholder="Full name of the athlete"
                      value={spotlightData.athleteName}
                      onChange={(e) => handleFieldChange('athleteName', e.target.value)}
                      data-error-field="athleteName"
                    />
                    {modalErrors.athleteName && (
                      <div className="error-message">{modalErrors.athleteName}</div>
                    )}
                  </div>

                  <div className="spotlight-editor-section" style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <label className="spotlight-editor-sublabel">Primary Sport *</label>
                      <input
                        type="text"
                        className={`spotlight-editor-input ${modalErrors.primarySport ? 'error' : ''}`}
                        placeholder="e.g., Basketball"
                        value={spotlightData.primarySport}
                        onChange={(e) => handleFieldChange('primarySport', e.target.value)}
                        data-error-field="primarySport"
                      />
                      {modalErrors.primarySport && (
                        <div className="error-message">{modalErrors.primarySport}</div>
                      )}
                    </div>
                  </div>

                  <div className="spotlight-editor-section" style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <label className="spotlight-editor-sublabel">City</label>
                      <input
                        type="text"
                        className="spotlight-editor-input"
                        placeholder="City"
                        value={spotlightData.city}
                        onChange={(e) => handleFieldChange('city', e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className="spotlight-editor-sublabel">State</label>
                      <input
                        type="text"
                        className="spotlight-editor-input"
                        placeholder="State"
                        value={spotlightData.state}
                        onChange={(e) => handleFieldChange('state', e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}



              {/* Spotlight Video */}
              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Spotlight Video (Optional)</label>

                {/* Show video preview - nomination video (Cloudflare iframe) */}
                {spotlightData.nominationVideoUid && !spotlightData.uploadedVideoFile && (
                  <div className="spotlight-video-preview-box" style={{ position: 'relative' }}>
                    <iframe
                      src={`https://iframe.cloudflarestream.com/${spotlightData.nominationVideoUid}`}
                      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                      allowFullScreen
                      style={{
                        border: 'none',
                        width: '100%',
                        height: '300px',
                        borderRadius: '8px'
                      }}
                    />
                    <button
                      className="spotlight-video-remove-btn"
                      onClick={() => {
                        setSpotlightData(prev => ({
                          ...prev,
                          nominationVideoUid: null,
                          uploadedVideoUid: null,
                          uploadedVideoFile: null,
                          uploadedVideoPreview: null
                        }));
                        setVideoUploadProgress(0);
                      }}
                      title="Remove video"
                    >
                      <img src="/assets/images/minus-icon.png" alt="Remove" />
                    </button>
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <p className="spotlight-editor-media-note" style={{ margin: 0, flex: 1 }}>
                        Video from nomination will be included in the spotlight.
                      </p>
                      <button
                        className="spotlight-editor-select-btn"
                        onClick={() => document.getElementById('video-input').click()}
                        style={{ fontSize: '13px', padding: '6px 14px' }}
                      >
                        Replace Video
                        <img src="/assets/images/upload-icon1.png" alt="Upload" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Show video preview - uploaded file (HTML5 video) */}
                {spotlightData.uploadedVideoFile && (
                  <div className="spotlight-video-preview-box" style={{ position: 'relative' }}>
                    <video
                      src={spotlightData.uploadedVideoPreview}
                      controls
                      style={{
                        width: '100%',
                        height: '300px',
                        borderRadius: '8px',
                        backgroundColor: '#000'
                      }}
                    />
                    <button
                      className="spotlight-video-remove-btn"
                      onClick={() => {
                        if (spotlightData.uploadedVideoPreview) {
                          URL.revokeObjectURL(spotlightData.uploadedVideoPreview);
                        }
                        setSpotlightData(prev => ({
                          ...prev,
                          uploadedVideoFile: null,
                          uploadedVideoPreview: null,
                          uploadedVideoUid: null
                        }));
                        setVideoUploadProgress(0);
                      }}
                      title="Remove video"
                    >
                      <img src="/assets/images/minus-icon.png" alt="Remove" />
                    </button>
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <p className="spotlight-editor-media-note" style={{ margin: 0, flex: 1 }}>
                        Video ready. Will be uploaded when you publish the spotlight.
                      </p>
                      <button
                        className="spotlight-editor-select-btn"
                        onClick={() => document.getElementById('video-input').click()}
                        style={{ fontSize: '13px', padding: '6px 14px' }}
                      >
                        Replace Video
                        <img src="/assets/images/upload-icon1.png" alt="Upload" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Show upload section if no video */}
                {!spotlightData.nominationVideoUid && !spotlightData.uploadedVideoFile && (
                  <div className="spotlight-editor-upload-area" onClick={() => document.getElementById('video-input').click()}>
                    <div className="spotlight-editor-upload-icon">
                      <img src="/assets/images/upload-icon.png" alt="Upload" />
                    </div>
                    <p className="spotlight-editor-upload-text">Click to upload video</p>
                    <p className="spotlight-editor-upload-subtext">MP4, MOV up to 200MB</p>
                    <button className="spotlight-editor-select-btn" onClick={(e) => { e.stopPropagation(); document.getElementById('video-input').click(); }}>
                      Select Video
                      <img src="/assets/images/upload-icon1.png" alt="Upload" />
                    </button>
                  </div>
                )}

                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-m4v"
                  onChange={handleVideoChange}
                  style={{ display: 'none' }}
                  id="video-input"
                />
              </div>

              {/* Spotlight Images */}
              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Spotlight Images *</label>
                <p className="spotlight-editor-media-note">
                  {spotlightData.momentMedia && spotlightData.momentMedia.length > 0
                    ? 'Select one image as the hero image. You can upload up to 3 images total.'
                    : 'Upload 1-3 images and select one as the hero image.'}
                </p>
                <div className="spotlight-editor-media-grid">
                  {spotlightData.momentMedia && spotlightData.momentMedia.map((media, index) => (
                    <div key={index} className={`spotlight-editor-media-item ${spotlightData.selectedHeroImageIndex === index ? 'hero-selected' : ''}`}>
                      <img
                        src={media.url || media.preview}
                        alt={`Image ${index + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {/* Hero Badge */}
                      {spotlightData.selectedHeroImageIndex === index && (
                        <div className="hero-badge">Hero Image</div>
                      )}
                      {/* Set as Hero Button */}
                      <button
                        className="spotlight-editor-media-hero-btn"
                        onClick={() => {
                          setSpotlightData(prev => ({
                            ...prev,
                            selectedHeroImageIndex: index
                          }));
                        }}
                      >
                        {spotlightData.selectedHeroImageIndex === index ? '★ Hero' : 'Set as Hero'}
                      </button>
                      {/* Remove Button */}
                      <button
                        className="spotlight-editor-media-remove"
                        onClick={() => {
                          setSpotlightData(prev => ({
                            ...prev,
                            momentMedia: prev.momentMedia.filter((_, i) => i !== index),
                            selectedHeroImageIndex: prev.selectedHeroImageIndex === index ? null :
                              prev.selectedHeroImageIndex > index ? prev.selectedHeroImageIndex - 1 : prev.selectedHeroImageIndex
                          }));
                        }}
                      >
                        <img src="/assets/images/minus-icon.png" alt="Remove" />
                      </button>
                    </div>
                  ))}
                  {(!spotlightData.momentMedia || spotlightData.momentMedia.length < 3) && (
                    <button
                      className="spotlight-editor-media-add"
                      onClick={() => document.getElementById('moment-media-input').click()}
                    >
                      <img src="/assets/images/plus-icon.png" alt="Add" />
                      <span>Add Image</span>
                    </button>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const currentCount = (spotlightData.momentMedia || []).length;
                      if (currentCount >= 3) { showNotification('error', 'Maximum 3 images allowed'); return; }
                      const reader = new FileReader();
                      reader.onload = () => {
                        setRawImageSrc(reader.result);
                        setCrop({ x: 0, y: 0 });
                        setZoom(1);
                        setCropModalOpen(true);
                      };
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }}
                    style={{ display: 'none' }}
                    id="moment-media-input"
                  />
                </div>
                {modalErrors.momentMedia && (
                  <div className="error-message">{modalErrors.momentMedia}</div>
                )}
                {modalErrors.heroImage && (
                  <div className="error-message">{modalErrors.heroImage}</div>
                )}
              </div>

              {/* Official Caption */}
              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Official Caption *</label>
                <textarea
                  className={`spotlight-editor-textarea ${modalErrors.officialCaption ? 'error' : ''}`}
                  placeholder="Write the official caption for this Spotlight..."
                  rows="4"
                  value={spotlightData.officialCaption}
                  onChange={(e) => handleFieldChange('officialCaption', e.target.value)}
                  data-error-field="officialCaption"
                ></textarea>
                {modalErrors.officialCaption && (
                  <div className="error-message">{modalErrors.officialCaption}</div>
                )}
              </div>

              {/* Time Label */}
              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Time Label</label>
                <input
                  type="text"
                  className="spotlight-editor-input"
                  placeholder="For example: Class of 2024, Spring 2023"
                  value={spotlightData.timeLabel}
                  onChange={(e) => handleFieldChange('timeLabel', e.target.value)}
                />
              </div>

              {/* Regional Spotlight Label */}
              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Regional Spotlight Label</label>
                <input
                  type="text"
                  className="spotlight-editor-input"
                  placeholder="For example: Temecula Valley Spotlight"
                  value={spotlightData.regionalLabel}
                  onChange={(e) => handleFieldChange('regionalLabel', e.target.value)}
                />
              </div>
            </div>

            <div className="spotlight-editor-footer">
              <button
                className="spotlight-editor-publish-btn"
                onClick={handleCreateSpotlight}
                disabled={actionLoading}
              >
                <img src="/assets/images/check-icon.png" alt="Publish" />
                {isUploadingVideo
                  ? `Uploading Video... ${videoUploadProgress}%`
                  : actionLoading
                    ? 'Publishing...'
                    : 'Publish Spotlight'}
              </button>
              <button
                className="spotlight-editor-cancel-btn"
                onClick={handleCloseSpotlightModal}
                disabled={actionLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* YouTube Video Preview Modal */}
      {showVideoPreview && previewVideoUrl && (
        <>
          <div className="nomination-modal-overlay" onClick={() => setShowVideoPreview(false)}></div>
          <div className="nomination-video-preview-modal">
            <button className="nomination-video-close" onClick={() => setShowVideoPreview(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="nomination-video-container">
              <iframe
                src={`https://www.youtube.com/embed/${getYouTubeVideoId(previewVideoUrl)}?autoplay=1&rel=0&modestbranding=1&controls=1`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '12px'
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Photo Gallery Modal */}
      {showPhotoGallery && photoGalleryImages.length > 0 && (
        <>
          <div className="nomination-modal-overlay" onClick={() => setShowPhotoGallery(false)}></div>
          <div className="nomination-photo-gallery-modal">
            <button className="nomination-gallery-close" onClick={() => setShowPhotoGallery(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Image Counter */}
            <div className="nomination-gallery-counter">
              {currentPhotoIndex + 1} / {photoGalleryImages.length}
            </div>

            {/* Navigation Arrows (only show if more than 1 photo) */}
            {photoGalleryImages.length > 1 && (
              <>
                <button className="nomination-gallery-nav nomination-gallery-prev" onClick={handlePrevPhoto}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6"/>
                  </svg>
                </button>
                <button className="nomination-gallery-nav nomination-gallery-next" onClick={handleNextPhoto}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </button>
              </>
            )}

            {/* Image Display */}
            <div className="nomination-gallery-image-container">
              <img
                src={photoGalleryImages[currentPhotoIndex]}
                alt={`Athlete ${currentPhotoIndex + 1}`}
                style={{
                  maxWidth: '90%',
                  maxHeight: '90%',
                  objectFit: 'contain',
                  borderRadius: '12px'
                }}
                onError={(e) => {
                  e.target.alt = 'Failed to load image';
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Cloudflare Video Preview Modal */}
      {showCloudflareVideoPreview && previewCloudflareVideoUid && (
        <>
          <div className="nomination-modal-overlay" onClick={() => setShowCloudflareVideoPreview(false)}></div>
          <div className="nomination-video-preview-modal">
            <button className="nomination-video-close" onClick={() => setShowCloudflareVideoPreview(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="nomination-video-container">
              <iframe
                src={`https://iframe.cloudflarestream.com/${previewCloudflareVideoUid}`}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                style={{
                  border: 'none',
                  width: '100%',
                  height: '100%',
                  borderRadius: '12px'
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NominationScreen;